import { highlightReportEvidence } from './reportEmphasis';
import { highlightReportKeywords } from './reportKeywords';

// Screen-only presentation, called after the existing field and heading parsers.
// Neither report storage nor the PDF adapter consumes this transformation.
export const reportHeadingTone = (text) => Array.from(String(text).trim().toUpperCase())
  .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0) % 5;

const protectedPeriod = (text, at) => {
  const before = text.slice(0, at);
  if (text[at - 1] === '.' || text[at + 1] === '.') return true;
  if (/\b(?:Dr|Mr|Mrs|Ms|Prof|Sr|Jr|St|vs|etc|e\.g|i\.e|No)\.$/i.test(text.slice(0, at + 1))) return true;
  if (/(?:^|[\s“"(])(?:[A-Z]\.)*[A-Z]$/.test(before)) return true;
  // Structural prefixes, including lower-case lettered headings.
  if (/(?:^|\n)\s*(?:\d+|[a-z])$/.test(before)) return true;
  return false;
};

export const sentenceWhitespaceRanges = (text) => {
  const result = [];
  // Keep closing quotes/brackets and attached evidence references with the sentence.
  const boundary = /\.(["'”’»\])}]*)((?:[ \t]*(?:\[[^\]\n]+\]|【[^】\n]+】|\((?:[^)\n]*\d[^)\n]*|(?:Point|Evidence|Source|Section)\b[^)\n]*)\)))*)(["'”’»\])}]*)(?:\.)?(\s+)(?=["'“‘(\[]*[\p{L}\p{N}])/gu;
  let match;
  while ((match = boundary.exec(text))) {
    if (protectedPeriod(text, match.index)) continue;
    const start = match.index + match[0].length - match[4].length;
    result.push([start, start + match[4].length]);
  }
  return result;
};

export const formatReportBodyDisplay = (html, sourceResponses = []) => {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const headings = '.phase5-field-label,.phase5-label,.phase5-subheading,.phase5-subheading-block,.nexus-content-heading,h1,h2,h3,h4,h5,h6';
  doc.body.querySelectorAll(headings).forEach(node => node.setAttribute('data-report-tone', reportHeadingTone(node.textContent)));
  const excluded = `${headings},summary,button,pre,code,script,style,.report-field-space`;
  const block = /^(P|DIV|LI|TD|TH|BLOCKQUOTE|UL|OL|TABLE|H[1-6])$/;
  let nodes = [];
  const flush = () => {
    if (!nodes.length) return;
    let text = ''; const entries = nodes.map(node => { const start = text.length; text += node.nodeValue; return { node, start, end: text.length }; });
    sentenceWhitespaceRanges(text).reverse().forEach(([start, end]) => {
      // Existing display separators are already complete: repeated calls are inert.
      const touched = entries.filter(e => e.end > start && e.start < end);
      if (touched.some(e => e.node.parentElement.closest('.report-sentence-space,.report-sentence-continuation'))) return;
      touched.reverse().forEach(({ node, start: offset, end: limit }) => {
        const from = Math.max(start, offset) - offset; const to = Math.min(end, limit) - offset;
        const selected = node.splitText(from); selected.splitText(to - from);
        const span = doc.createElement('span'); span.className = offset <= start ? 'report-sentence-space' : 'report-sentence-continuation';
        selected.replaceWith(span); span.append(selected);
      });
    });
    nodes = [];
  };
  const walk = node => {
    if (node.nodeType === 3) { nodes.push(node); return; }
    if (node.nodeType !== 1) return;
    if (node.matches(excluded)) { flush(); return; }
    const boundary = block.test(node.tagName) || node.matches('.report-field,.report-field-body') || node.tagName === 'BR';
    if (boundary) flush();
    Array.from(node.childNodes).forEach(walk);
    if (boundary) flush();
  };
  walk(doc.body); flush();
  highlightReportEvidence(doc.body, excluded, sourceResponses);
  highlightReportKeywords(doc.body, excluded);
  return doc.body.innerHTML;
};
